import type { Vec3 } from '../math';
import type { PatchCollide } from './patch';

export const PLANE_X = 0;
export const PLANE_Y = 1;
export const PLANE_Z = 2;
export const PLANE_NON_AXIAL = 3;

export interface CPlane {
  normal: Vec3;
  dist: number;
  /** 0..2 for axial planes, PLANE_NON_AXIAL otherwise. */
  type: number;
  /** Bit i set when normal[i] < 0; selects the box corner used for expansion. */
  signbits: number;
}

export interface CBrushSide {
  plane: CPlane;
  surfaceFlags: number;
}

export interface CBrush {
  contents: number;
  sides: CBrushSide[];
  mins: Vec3;
  maxs: Vec3;
  checkcount: number;
}

export interface CPatch {
  /** Index of the BSP surface the patch was built from. */
  surface: number;
  contents: number;
  surfaceFlags: number;
  pc: PatchCollide;
  checkcount: number;
}

export interface CNode {
  plane: CPlane;
  children: [number, number];
}

export interface CLeaf {
  brushes: number[];
  patches: number[];
}

export interface CModel {
  mins: Vec3;
  maxs: Vec3;
  /** Leaf holding the brushes of an inline (entity) model. */
  leaf: CLeaf;
}

export interface TraceResult {
  allsolid: boolean;
  startsolid: boolean;
  /** 0..1, 1 = nothing hit. */
  fraction: number;
  endpos: Vec3;
  plane: { normal: Vec3; dist: number };
  surfaceFlags: number;
  contents: number;
  /** Entity that was hit (0 = world). Filled in by the game world layer. */
  entityNum: number;
}

export function makePlane(normal: Vec3, dist: number): CPlane {
  let type = PLANE_NON_AXIAL;
  if (normal[0] === 1 || normal[0] === -1) type = PLANE_X;
  else if (normal[1] === 1 || normal[1] === -1) type = PLANE_Y;
  else if (normal[2] === 1 || normal[2] === -1) type = PLANE_Z;
  // Axial "type" is only valid for positive axial normals in the BSP tree
  // traversal code path (it reads p[type] - dist), so restrict it.
  if (type !== PLANE_NON_AXIAL && normal[type] !== 1) type = PLANE_NON_AXIAL;
  let signbits = 0;
  for (let i = 0; i < 3; i++) if (normal[i] < 0) signbits |= 1 << i;
  return { normal, dist, type, signbits };
}
